/**
 * One unit number per block — the browser-side half of the rule.
 *
 * The authority is the database (condo-manager's services/unit_number.go, which runs the same
 * normalisation in SQL so a concurrent create cannot slip between the check and the insert).
 * This exists so a síndico typing a number their quadra already has is told while the field is
 * still focused, instead of by a 409 after submitting.
 *
 * Both sides MUST agree. `normalizeUnitNumber` here is `NormalizeUnitNumber` there: trim, collapse
 * runs of inner whitespace, lower-case — and nothing else. In particular leading zeros and accents
 * are kept, because "01" and "1", "Lote Ú" and "Lote U" are genuinely different lotes in plenty of
 * condomínios, and merging two real homes is worse than letting both exist.
 *
 * This is the COMPARISON KEY, never the stored value: what the síndico typed is what is shown.
 */
export function normalizeUnitNumber(number: string): string {
  return (number || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Whether `candidate` names a unit the block already has.
 *
 * A blank candidate is never "taken" — an empty field is the "obrigatório" error, a different
 * message. Null/undefined entries are tolerated because the list comes straight off the API.
 */
export function isUnitNumberTaken(existingNumbers: string[], candidate: string): boolean {
  const key = normalizeUnitNumber(candidate);
  if (!key) return false;
  return (existingNumbers || []).some((existing) => normalizeUnitNumber(existing || '') === key);
}

/** The minimum a unit row needs to take part in the duplicate check. */
export interface UnitNumberCandidate {
  id: string;
  block_id: string;
  number: string;
  is_active?: boolean;
}

/**
 * The unit already holding `candidate` in `blockID`, if any.
 *
 * Scoped to the block, because every condomínio with more than one quadra has a 101 in each.
 * Deactivated units are ignored: that is how a condomínio renumbers without carrying its history
 * forever, and the backend's check is `is_active = true` for the same reason. `excludeId` is the
 * unit being renamed — saving a unit with its own number unchanged is not a conflict.
 */
export function duplicateUnitInBlock<T extends UnitNumberCandidate>(
  units: T[],
  blockId: string,
  candidate: string,
  excludeId?: string
): T | undefined {
  const key = normalizeUnitNumber(candidate);
  if (!key) return undefined;
  return (units || []).find(
    (unit) =>
      unit &&
      unit.block_id === blockId &&
      unit.is_active !== false &&
      unit.id !== excludeId &&
      normalizeUnitNumber(unit.number || '') === key
  );
}
